import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiCameraOutline } from "@mdi/js";
import "./ImageUpload.css";
/**
 * ImageUpload component with compression logic
 * Extracted from AdminConsole.js for reusability
 * Maintains same compression settings (0.1 quality, 800px max width)
 */
const ImageUpload = ({ 
    onImageSelect, 
    currentImage = null, 
    placeholder = "Click to Upload Image",
    maxWidth = 800,
    maxHeight = 600,
    compressionQuality = 0.1,
    thumbnailQuality = 0.3
}) => {
    const [isProcessing, setIsProcessing] = useState(false);

    // Compress image - exact logic from AdminConsole
    const compressImage = (file, quality = 0.1) => {
        return new Promise(resolve => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();

            img.onload = () => {
                // Calculate new dimensions (max 800px width to match mobile app sizing)
                let { width, height } = img;

                if (width > height) {
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to base64 with compression (matching mobile app's 0.1 quality)
                const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
                resolve(compressedBase64);
            };

            img.src = URL.createObjectURL(file);
        });
    };

    // Handle image upload - exact logic from AdminConsole
    const handleImageUpload = async (file) => {
        if (!file) return;

        setIsProcessing(true);

        try {
            // Compress image similar to mobile app
            const compressedBase64 = await compressImage(file, compressionQuality);

            // Create thumbnail for display
            const thumbnailBase64 = await compressImage(file, thumbnailQuality);

            const imageData = {
                file,
                compressedBase64,
                thumbnail: thumbnailBase64,
            };

            onImageSelect(imageData);
        } catch (error) {
            console.error("Error compressing image:", error);
            onImageSelect(null, error);
        } finally {
            setIsProcessing(false);
        }
    };

    const inputId = `image-upload-${Math.random().toString(36).substr(2, 9)}`;

    return (
        <div className={`admin-console-image-upload-container${currentImage ? ' has-image' : ''}`}>
            {currentImage ? (
                <>
                    <img
                        src={currentImage.thumbnail || currentImage}
                        alt="Uploaded thumbnail"
                        className="admin-console-thumbnail"
                    />
                    <span
                        className="admin-console-change-image-link"
                        onClick={() => document.getElementById(inputId).click()}
                    >
                        {isProcessing ? "Processing..." : "Change"}
                    </span>
                </>
            ) : (
                <label
                    htmlFor={inputId}
                    className="admin-console-no-image-uploads"
                    style={{ opacity: isProcessing ? 0.6 : 1 }}
                >
                    <Icon path={mdiCameraOutline} size={1.5} color="var(--dl-color-purple-default)" />
                    <small>
                        {isProcessing ? "Processing..." : placeholder}
                    </small>
                </label>
            )}
            <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                className="admin-console-image-input"
                id={inputId}
                disabled={isProcessing}
            />
        </div>
    );
};

export default ImageUpload;